import { forEach, isEmpty } from 'ramda';
import { projectSlice } from '../redux/ProjectSlice';
import { BATTERIES, BATTERY_TYPE, DEVELOPMENT, RobotActions } from '../utils/constants';
import { getLEDObjects } from '../utils/helpers';

const RobotInterface = (robot, robotType, store) => {
  const toy = {
    _robot: robot,
    robotType,
  };
  const motorPlusSettingsUpdateFields = [
    'targetPosition',
    'targetVelocity',
    'targetDuty',
    'targetTimeout',
    'targetTolerance',
    'maxTorque',
    'polarityInversion',
    'stopMode',
    'motorMode',
  ];
  const pendingGetMotorPlusSettingsRequests = [];
  const pendingSetMotorPlusSettingsRequests = [];
  let activeGetMotorPlusSettingsRequest = null;
  let activeSetMotorPlusSettingsRequest = null;

  const { dispatch } = store;

  toy.sdkToy = () => toy._robot;

  /* Shorthand properties */
  toy.LED = robot.LED || {}; // Default value is {} for unsupported robots
  toy.ANIMATION = robot.ANIMATION || {};
  toy.EVENT = robot.EVENT || {};
  toy._on = robot.on.bind(robot);
  toy._off = robot.off.bind(robot);
  toy._once = robot.once.bind(robot);
  toy.ledControl = robot.ledControl || {};
  toy.getName = () => robot.getName();
  toy.sensorInterval = 150;
  toy.colorPalette = [];

  toy.supportedCommands = robot.getSupportedCommands();
  toy.supportedLedCommands = robot.ledControl.getSupportedCommands();

  toy.hasCommand = (commandName) => toy.supportedCommands.includes(commandName);
  toy.hasLedCommand = (commandName) => toy.supportedLedCommands.includes(commandName);

  /* Targets */
  toy.hasTargets = robot.PROCESSORS !== undefined;
  toy.primaryTargetId = toy.hasTargets ? robot.PROCESSORS.primary : undefined;
  toy.secondaryTargetId = toy.hasTargets ? robot.PROCESSORS.secondary : undefined;

  /* Notifications */
  toy.gyroMaxNotification = toy.EVENT.GYRO_MAX_NOTIFY;
  toy.sensorDataReadyNotification = toy.EVENT.SENSOR_CONTROL.DATA_READY;

  // BPR DU
  toy.consoleStringNotification = toy.EVENT.SEND_STRING_TO_CONSOLE;
  toy.pythonPrintNotification = toy.EVENT.PYTHON_PRINT_TO_CONSOLE;
  toy.pythonStreamVariableNotification = toy.EVENT.PYTHON_STREAM_VARIABLE_DATA;
  toy.pythonStreamItemsAddedNotification = toy.EVENT.NOTIFY_STREAMING_ITEMS_ADDED;

  /* Responses */
  toy.setHandshakeIdNotification = toy.EVENT.SET_HANDSHAKE_ID;
  toy.getHandshakeIdResponse = toy.EVENT.GET_HANDSHAKE_ID;
  toy.getSkuResponse = toy.EVENT.GET_SKU;
  toy.setSkuNotification = toy.EVENT.SET_SKU;

  const getFuelGaugeStateResponse = () => {
    if (toy.hasCommand('getFuelGaugeState')) {
      return toy.EVENT.RESPONSE.GET_FUEL_GAUGE_STATE;
    }
  };
  toy.getFuelGaugeStateResponse = getFuelGaugeStateResponse();

  const getBoardRevisionResponse = () => {
    if (toy.hasCommand('getBoardRevision')) {
      return toy.EVENT.RESPONSE.GET_BOARD_REVISION;
    }
  };
  toy.getBoardRevisionResponse = getBoardRevisionResponse();

  const getManufacturingDateResponse = () => {
    if (toy.hasCommand('getManufacturingDate')) {
      return toy.EVENT.RESPONSE.GET_MANUFACTURING_DATE;
    }
  };
  toy.getManufacturingDateResponse = getManufacturingDateResponse();

  const getMacAddressResponse = () => {
    if (toy.hasCommand('getMacAddress')) {
      return toy.EVENT.RESPONSE.GET_MAC_ADDRESS;
    }
  };
  toy.getMacAddressResponse = getMacAddressResponse();

  const getUidResponse = () => {
    if (toy.hasCommand('getUid')) {
      return toy.EVENT.RESPONSE.GET_UID;
    }
  };
  toy.getUidResponse = getUidResponse();

  const getSkuResponse = () => {
    if (toy.hasCommand('getSku')) {
      return toy.EVENT.RESPONSE.GET_SKU;
    }
  };
  toy.getSkuResponse = getSkuResponse();

  const getBluetoothAdvertisingNameResponse = () => {
    if (toy.hasCommand('getBluetoothAdvertisingName')) {
      return toy.EVENT.RESPONSE.GET_BLUETOOTH_ADVERTISING_NAME;
    }
  };
  toy.getBluetoothAdvertisingNameResponse = getBluetoothAdvertisingNameResponse();

  const getCurrentApplicationIdResponse = () => {
    if (toy.hasCommand('getCurrentApplicationId')) {
      return toy.EVENT.RESPONSE.GET_CURRENT_APPLICATION_ID;
    }
  };
  toy.getCurrentApplicationIdResponse = getCurrentApplicationIdResponse();

  const getSystemStateResponse = () => {
    // Drivebase system state (controller + DU battery)
    if (toy.hasCommand('getDrivebaseSystemState')) {
      return toy.EVENT.RESPONSE.GET_DRIVEBASE_SYSTEM_STATE;
    }
  };
  toy.getSystemStateResponse = getSystemStateResponse();

  const getExecutingPythonTypeAndNameResponse = () => {
    if (toy.hasCommand('getExecutingPythonTypeAndName')) {
      return toy.EVENT.RESPONSE.GET_EXECUTING_PYTHON_TYPE_AND_NAME;
    }
  };
  toy.getExecutingPythonTypeAndNameResponse = getExecutingPythonTypeAndNameResponse();

  const getPythonProgramNameAtIndexResponse = () => {
    if (toy.hasCommand('getPythonProgramNameAtIndex')) {
      return toy.EVENT.RESPONSE.GET_PYTHON_PROGRAM_NAME_AT_INDEX;
    }
  };
  toy.getPythonProgramNameAtIndexResponse = getPythonProgramNameAtIndexResponse();

  const getNumberOfStreamedItemsResponse = () => {
    if (toy.hasCommand('getNumberOfPythonStreamedItems')) {
      return toy.EVENT.RESPONSE.GET_NUMBER_OF_PYTHON_STREAMED_ITEMS;
    }
  };
  toy.getNumberOfStreamedItemsResponse = getNumberOfStreamedItemsResponse();

  const getStreamItemDataResponse = () => {
    if (toy.hasCommand('getPythonStreamItemData')) {
      return toy.EVENT.RESPONSE.GET_PYTHON_STREAM_ITEM_DATA;
    }
  };
  toy.getStreamItemDataResponse = getStreamItemDataResponse();

  const getRemoteDeviceTypesResponse = () => {
    if (toy.hasCommand('getRemoteDeviceTypes')) {
      return toy.EVENT.RESPONSE.GET_REMOTE_DEVICE_TYPES;
    }
  };
  toy.getRemoteDeviceTypesResponse = getRemoteDeviceTypesResponse();

  const getRemoteDeviceSystemInfoByIndexResponse = () => {
    if (toy.hasCommand('getRemoteDeviceSystemInfoByIndex')) {
      return toy.EVENT.RESPONSE.GET_REMOTE_DEVICE_SYSTEM_INFO_BY_INDEX;
    }
  };
  toy.getRemoteDeviceSystemInfoByIndexResponse = getRemoteDeviceSystemInfoByIndexResponse();

  const getDistanceSensorSettingsResponse = () => {
    if (toy.hasCommand('getDistanceSensorSettings')) {
      return toy.EVENT.RESPONSE.GET_DISTANCE_SENSOR_SETTINGS;
    }
  };
  toy.getDistanceSensorSettingsResponse = getDistanceSensorSettingsResponse();

  const getBeamBreakSensorSettingsResponse = () => {
    if (toy.hasCommand('getBeamBreakSensorSettings')) {
      return toy.EVENT.RESPONSE.GET_BEAM_BREAK_SENSOR_SETTINGS;
    }
  };
  toy.getBeamBreakSensorSettingsResponse = getBeamBreakSensorSettingsResponse();

  const getMagneticSwitchSettingsResponse = () => {
    if (toy.hasCommand('getMagneticSwitchSettings')) {
      return toy.EVENT.RESPONSE.GET_MAGNETIC_SWITCH_SETTINGS;
    }
  };
  toy.getMagneticSwitchSettingsResponse = getMagneticSwitchSettingsResponse();

  const getBeeAdapterSettingsResponse = () => {
    if (toy.hasCommand('getBeeAdapterSettings')) {
      return toy.EVENT.RESPONSE.GET_BEE_ADAPTER_SETTINGS;
    }
  };
  toy.getBeeAdapterSettingsResponse = getBeeAdapterSettingsResponse();

  const getLibraryUpdaterStateDeprecatedResponse = () => {
    if (toy.hasCommand('getLibraryUpdaterStateDeprecated')) {
      return toy.EVENT.RESPONSE.GET_LIBRARY_UPDATER_STATE_DEPRECATED;
    }
  };
  toy.getLibraryUpdaterStateDeprecatedResponse = getLibraryUpdaterStateDeprecatedResponse();

  const getLibraryUpdaterProgressResponse = () => {
    if (toy.hasCommand('getLibraryUpdaterProgress')) {
      return toy.EVENT.RESPONSE.GET_LIBRARY_UPDATER_PROGRESS;
    }
  };
  toy.getLibraryUpdaterProgressResponse = getLibraryUpdaterProgressResponse();

  const getMotorPlusSettingsResponse = () => {
    if (toy.hasCommand('getMotorPlusSettings')) {
      return toy.EVENT.RESPONSE.GET_MOTOR_PLUS_SETTINGS;
    }
  };
  toy.getMotorPlusSettingsResponse = getMotorPlusSettingsResponse();

  const setMotorPlusSettingsResponse = () => {
    if (toy.hasCommand('setMotorPlusSettings')) {
      return toy.EVENT.RESPONSE.SET_MOTOR_PLUS_SETTINGS;
    }
  };
  toy.setMotorPlusSettingsResponse = setMotorPlusSettingsResponse();

  const runNextGetMotorPlusSettingsRequest = () => {
    if (activeGetMotorPlusSettingsRequest || !pendingGetMotorPlusSettingsRequests.length || !toy.getMotorPlusSettingsResponse) {
      return;
    }

    const nextRequest = pendingGetMotorPlusSettingsRequests.shift();
    activeGetMotorPlusSettingsRequest = nextRequest;

    toy._once(toy.getMotorPlusSettingsResponse, (response) => {
      const settledRequest = activeGetMotorPlusSettingsRequest;
      activeGetMotorPlusSettingsRequest = null;
      settledRequest?.resolve(response);
      runNextGetMotorPlusSettingsRequest();
    });

    try {
      toy._robot.getMotorPlusSettings(nextRequest.sdkPort);
    } catch (error) {
      activeGetMotorPlusSettingsRequest = null;
      nextRequest.reject(error);
      runNextGetMotorPlusSettingsRequest();
    }
  };

  const runNextSetMotorPlusSettingsRequest = () => {
    if (activeSetMotorPlusSettingsRequest || !pendingSetMotorPlusSettingsRequests.length || !toy.setMotorPlusSettingsResponse) {
      return;
    }

    const nextRequest = pendingSetMotorPlusSettingsRequests.shift();
    activeSetMotorPlusSettingsRequest = nextRequest;

    toy._once(toy.setMotorPlusSettingsResponse, (response) => {
      const settledRequest = activeSetMotorPlusSettingsRequest;
      activeSetMotorPlusSettingsRequest = null;
      settledRequest?.resolve(response);
      runNextSetMotorPlusSettingsRequest();
    });

    try {
      toy._robot.setMotorPlusSettings(...nextRequest.sdkArguments);
    } catch (error) {
      activeSetMotorPlusSettingsRequest = null;
      nextRequest.reject(error);
      runNextSetMotorPlusSettingsRequest();
    }
  };

  const validateMotorPlusSettingsUpdate = (settings) => {
    const normalizedSettings = { ...(settings ?? {}) };
    const missingFields = motorPlusSettingsUpdateFields.filter((field) => normalizedSettings[field] == null);

    if (missingFields.length > 0) {
      throw new Error(`Motor Plus settings updates must include ${missingFields.join(', ')}.`);
    }

    return normalizedSettings;
  };

  toy.addEventsListener = (listener) => {
    /* Responses */
    toy._on(toy.getFuelGaugeStateResponse, listener.onGetFuelGaugeStateResponseHandler);
    toy._on(toy.getBoardRevisionResponse, listener.onGetBoardRevisionResponseHandler);
    toy._on(toy.getUidResponse, listener.onGetUidResponseHandler);
    if (toy.getSystemStateResponse) {
      toy._on(toy.getSystemStateResponse, listener.onGetSystemStateResponseHandler);
    }
    if (toy.getCurrentApplicationIdResponse) {
      toy._on(toy.getCurrentApplicationIdResponse, listener.onGetCurrentApplicationIdResponseHandler);
    }

    // BPR DU
    toy._on(toy.getNumberOfStreamedItemsResponse, listener.onGetNumberOfStreamedItemsResponseHandler);
    toy._on(toy.getStreamItemDataResponse, listener.onGetStreamItemDataResponseHandler);
    toy._on(toy.getRemoteDeviceTypesResponse, listener.onGetRemoteDeviceTypesResponseHandler);
    toy._on(toy.getRemoteDeviceSystemInfoByIndexResponse, listener.onGetRemoteDeviceSystemInfoByIndexResponseHandler);
    toy._on(toy.getDistanceSensorSettingsResponse, listener.onGetDistanceSensorSettingsResponseHandler);
    toy._on(toy.getBeamBreakSensorSettingsResponse, listener.onGetBeamBreakSensorSettingsResponseHandler);
    toy._on(toy.getMagneticSwitchSettingsResponse, listener.onGetMagneticSwitchSettingsResponseHandler);
    toy._on(toy.getBeeAdapterSettingsResponse, listener.onGetBeeAdapterSettingsResponseHandler);
    toy._on(toy.consoleStringNotification, listener.onConsoleStringNotificationHandler);
    toy._on(toy.pythonPrintNotification, listener.onPythonPrintNotificationHandler);
    toy._on(toy.pythonStreamVariableNotification, listener.onPythonStreamVariableNotificationHandler);
    toy._on(toy.pythonStreamItemsAddedNotification, listener.onPythonStreamItemsAddedNotificationHandler);
    toy._on(toy.getLibraryUpdaterProgressResponse, listener.onGetLibraryUpdaterProgressResponseHandler);
    toy._on(toy.getLibraryUpdaterStateDeprecatedResponse, listener.onGetLibraryUpdaterStateDeprecatedResponseHandler);
    if (toy.getMotorPlusSettingsResponse) {
      toy._on(toy.getMotorPlusSettingsResponse, listener.onGetMotorPlusSettingsResponseHandler);
    }
    if (toy.setMotorPlusSettingsResponse) {
      toy._on(toy.setMotorPlusSettingsResponse, listener.onSetMotorPlusSettingsResponseHandler);
    }

    // Listen for the "Set Handshake Id" event
    toy._on(toy.setHandshakeIdNotification, listener.onSetHandshakeIdNotification);
    toy._on(toy.getHandshakeIdResponse, listener.onGetHandshakeIdResponseHandler);
    toy._on(toy.getSkuResponse, listener.onGetSkuResponseHandler);
    if (toy.getBluetoothAdvertisingNameResponse) {
      toy._on(toy.getBluetoothAdvertisingNameResponse, listener.onGetBluetoothAdvertisingNameResponseHandler);
    }
    toy._on(toy.setSkuNotification, listener.onSetSkuNotificationHandler);
  };

  toy.removeEventsListener = (listener) => {
    // BPR DU
    toy._off(toy.getNumberOfStreamedItemsResponse, listener.onGetNumberOfStreamedItemsResponseHandler);
    toy._off(toy.getStreamItemDataResponse, listener.onGetStreamItemDataResponseHandler);
    toy._off(toy.getRemoteDeviceTypesResponse, listener.onGetRemoteDeviceTypesResponseHandler);
    toy._off(toy.getRemoteDeviceSystemInfoByIndexResponse, listener.onGetRemoteDeviceSystemInfoByIndexResponseHandler);
    toy._off(toy.getDistanceSensorSettingsResponse, listener.onGetDistanceSensorSettingsResponseHandler);
    toy._off(toy.getBeamBreakSensorSettingsResponse, listener.onGetBeamBreakSensorSettingsResponseHandler);
    toy._off(toy.getMagneticSwitchSettingsResponse, listener.onGetMagneticSwitchSettingsResponseHandler);
    toy._off(toy.getBeeAdapterSettingsResponse, listener.onGetBeeAdapterSettingsResponseHandler);
    toy._off(toy.consoleStringNotification, listener.onConsoleStringNotificationHandler);
    toy._off(toy.pythonPrintNotification, listener.onPythonPrintNotificationHandler);
    toy._off(toy.pythonStreamVariableNotification, listener.onPythonStreamVariableNotificationHandler);
    toy._off(toy.pythonStreamItemsAddedNotification, listener.onPythonStreamItemsAddedNotificationHandler);
    toy._off(toy.getLibraryUpdaterProgressResponse, listener.onGetLibraryUpdaterProgressResponseHandler);
    toy._off(toy.getLibraryUpdaterStateDeprecatedResponse, listener.onGetLibraryUpdaterStateDeprecatedResponseHandler);
    if (toy.getMotorPlusSettingsResponse) {
      toy._off(toy.getMotorPlusSettingsResponse, listener.onGetMotorPlusSettingsResponseHandler);
    }
    if (toy.setMotorPlusSettingsResponse) {
      toy._off(toy.setMotorPlusSettingsResponse, listener.onSetMotorPlusSettingsResponseHandler);
    }

    /* Responses */
    toy._off(toy.getFuelGaugeStateResponse, listener.onGetFuelGaugeStateResponseHandler);
    toy._off(toy.getBoardRevisionResponse, listener.onGetBoardRevisionResponseHandler);
    toy._off(toy.getUidResponse, listener.onGetUidResponseHandler);
    if (toy.getSystemStateResponse) {
      toy._off(toy.getSystemStateResponse, listener.onGetSystemStateResponseHandler);
    }
    toy._off(toy.setHandshakeIdNotification, listener.onSetHandshakeIdNotification);
    toy._off(toy.getHandshakeIdResponse, listener.onGetHandshakeIdResponseHandler);
    toy._off(toy.getSkuResponse, listener.onGetSkuResponseHandler);
    if (toy.getBluetoothAdvertisingNameResponse) {
      toy._off(toy.getBluetoothAdvertisingNameResponse, listener.onGetBluetoothAdvertisingNameResponseHandler);
    }
    toy._off(toy.setSkuNotification, listener.onSetSkuNotificationHandler);
  };

  toy.unableToRunCommands = (commands) => {
    if (DEVELOPMENT) {
      console.warn(`${toy.robotType} is unable to run command/s ${commands}`);
    }
  };

  /* Command wrappers */
  toy.setMainLED = (r, g, b) => {
    if (toy.hasCommand('setMainLed')) {
      toy._robot.setMainLed(r, g, b);
    } else {
      const colorObject = { r, g, b };
      const ledObject = getLEDObjects(toy, 'MAIN', colorObject);
      toy.setLED(ledObject);
    }
  };

  toy.setSingleLED = (led, r, g, b) => {
    const colorObject = { r, g, b };
    let ledObject = {};
    let ledString = led;
    if (led) {
      [ledString] = led.split('_');
    }
    ledObject = getLEDObjects(toy, ledString, colorObject);
    toy.setLED(ledObject);
  };

  toy.setLED = (ledObject, callback) => {
    delete ledObject[undefined];

    if (toy.hasLedCommand('setLeds') && !isEmpty(ledObject)) {
      toy._robot.ledControl.setLeds(ledObject);
    } else if (callback) {
      callback();
    } else {
      toy.unableToRunCommands('setLeds');
    }
  };

  toy.turnOnLights = (color) => {
    toy.setMainLED(color.r, color.g, color.b);
  };

  toy.turnOffLights = () => {
    const ledObject = {};
    const userLEDs = [toy.LED.userBodyRed, toy.LED.userBodyGreen, toy.LED.userBodyBlue];
    forEach((value) => {
      if (!userLEDs.includes(value)) ledObject[value] = 0;
    }, toy.LED);
    toy.setLED(ledObject, () => {
      toy.setMainLED(0, 0, 0);
      toy.setBackLEDBrightness(0);
    });
  };

  toy.rollToDistance = (heading, speed, distance) => {
    if (toy.hasCommand('driveDistanceAtYawNormalized')) {
      toy._robot.driveDistanceAtYawNormalized(heading, speed, distance);
    } else {
      toy.unableToRunCommands('driveDistanceAtYawNormalized');
    }
  };

  toy.rollForTime = (heading, speed, time) => {
    if (toy.hasCommand('driveTimeAtYawNormalized')) {
      toy._robot.driveTimeAtYawNormalized(heading, speed, time);
    } else {
      toy.unableToRunCommands('driveTimeAtYawNormalized');
    }
  };

  toy.driveRcNormalized = (yawAngularVelocity, linearVelocity) => {
    if (toy.hasCommand('driveRcNormalized')) {
      toy._robot.driveRcNormalized(yawAngularVelocity, linearVelocity, 1);
    } else {
      toy.unableToRunCommands('driveRcNormalized');
    }
  };

  toy.stopWithDefaultDeceleration = () => {
    if (toy.hasCommand('stopWithDefaultDeceleration')) {
      toy._robot.stopWithDefaultDeceleration();
    } else {
      toy.unableToRunCommands('stopWithDefaultDeceleration');
    }
  };

  toy.stopWithCustomDeceleration = (deaccelerationRate) => {
    if (toy.hasCommand('stopWithCustomDeceleration')) {
      toy._robot.stopWithCustomDeceleration(deaccelerationRate);
    } else {
      toy.unableToRunCommands('stopWithCustomDeceleration');
    }
  };

  toy.getActiveControlSystem = () => {
    if (toy.hasCommand('getActiveControlSystemId')) {
      toy._robot.getActiveControlSystemId();
    } else {
      toy.unableToRunCommands('getActiveControlSystemId');
    }
  };

  toy.setCommandQueue = (enabled) => {
    if (toy.hasCommand('setCommandQueue')) {
      toy._robot.setCommandQueue(enabled);
    } else {
      toy.unableToRunCommands('setCommandQueue');
    }
  };

  toy.setPersistentOptions = () => {
    if (toy.hasCommand('enableGyroMaxNotify')) {
      toy._robot.enableGyroMaxNotify(true);
    } else {
      toy.unableToRunCommands('enableGyroMaxNotify');
    }
  };

  toy.getBatteryLevel = () => {
    if (BATTERY_TYPE[robotType] === BATTERIES.PERCENTAGE && toy.hasCommand('getBatteryPercentage')) {
      toy._robot.getBatteryPercentage();
    } else if (toy.hasCommand('getBatteryVoltage')) {
      toy._robot.getBatteryVoltage();
    } else if (toy.hasCommand('getFuelGaugeState')) {
      toy._robot.getFuelGaugeState();
    } else {
      toy.unableToRunCommands(['getBatteryPercentage', 'getBatteryVoltage']);
    }
  };

  toy.ping = () => {
    if (toy.hasCommand('ping')) {
      toy._robot.ping();
    } else {
      toy.unableToRunCommands('ping');
    }
  };

  toy.getBoardRevision = () => {
    if (toy.hasCommand('getBoardRevision')) {
      toy._robot.getBoardRevision();
    } else {
      toy.unableToRunCommands('getBoardRevision');
    }
  };

  toy.getManufacturingDate = () => {
    if (toy.hasCommand('getManufacturingDate')) {
      toy._robot.getManufacturingDate();
    } else {
      toy.unableToRunCommands('getManufacturingDate');
    }
  };

  toy.getMacAddress = () => {
    if (toy.hasCommand('getMacAddress')) {
      toy._robot.getMacAddress();
    } else {
      toy.unableToRunCommands('getMacAddress');
    }
  };

  toy.getUid = () => {
    if (toy.hasCommand('getUid')) {
      toy._robot.getUid();
    } else {
      toy.unableToRunCommands('getUid');
    }
  };

  toy.getSku = () => {
    if (toy.hasCommand('getSku')) {
      toy._robot.getSku();
    } else {
      toy.unableToRunCommands('getSku');
    }
  };

  toy.getBluetoothAdvertisingName = () => {
    if (toy.hasCommand('getBluetoothAdvertisingName')) {
      toy._robot.getBluetoothAdvertisingName();
    } else {
      toy.unableToRunCommands('getBluetoothAdvertisingName');
    }
  };

  toy.getCurrentApplicationId = () => {
    if (toy.hasCommand('getCurrentApplicationId')) {
      toy._robot.getCurrentApplicationId();
    } else {
      toy.unableToRunCommands('getCurrentApplicationId');
    }
  };

  toy.getSystemState = () => {
    if (toy.hasCommand('getDrivebaseSystemState')) {
      toy._robot.getDrivebaseSystemState();
    } else {
      toy.unableToRunCommands('getDrivebaseSystemState');
    }
  };

  toy.setFrontLED = (r, g, b) => {
    let ledObject = {};
    const colorObject = { r, g, b };
    ledObject = getLEDObjects(toy, 'front', colorObject);
    toy.setLED(ledObject);
  };

  toy.setBackLED = (r, g, b) => {
    let ledObject = {};
    const colorObject = { r, g, b };
    ledObject = getLEDObjects(toy, 'back', colorObject);
    toy.setLED(ledObject);
  };

  toy.setBackLEDBrightness = (brightness) => {
    // None of the robots supported in web-ble have this specific command.
    // Instead we just want to set the brightness of the blue light for the back LED.
    toy.setBackLED(0, 0, brightness);
  };

  toy.enableThermalProtection = (enable) => {
    if (toy.hasCommand('enableMotorThermalProtectionStatusNotify')) {
      toy._robot.enableMotorThermalProtectionStatusNotify(enable);
    } else {
      toy.unableToRunCommands('enableMotorThermalProtectionStatusNotify');
    }
  };

  toy.enableStallNotify = (enable) => {
    if (toy.hasCommand('enableMotorStallNotify')) {
      toy._robot.enableMotorStallNotify(enable);
    } else {
      toy.unableToRunCommands('enableMotorStallNotify');
    }
  };

  toy.onConnect = () => {
    toy.setPersistentOptions();
    toy.setCommandQueue(true);

    toy.getSku();
    toy.getBoardRevision();
    toy.getUid();
    toy.getManufacturingDate();
    toy.getCurrentApplicationId();
  };

  toy.onDisconnect = () => {
    toy.enableThermalProtection(false);
    toy.enableStallNotify(false);
  };

  toy.onProgramLoad = () => {
    toy.setCommandQueue(false);
  };

  toy.onProgramStart = (slot, filename) => {
    toy.executePythonFile(slot, filename);
  };

  toy.onProgramStop = () => {
    dispatch(
      projectSlice.actions.onUpdateProgramState({
        programState: RobotActions.PROGRAM.STOPPED,
      })
    );
  };

  toy.abortPythonExecution = () => {
    if (toy.hasCommand('abortPythonExecution')) {
      toy._robot.abortPythonExecution();
    } else {
      toy.unableToRunCommands('abortPythonExecution');
    }
  };

  toy.executePythonFile = (slot, filename) => {
    if (toy.hasCommand('executePythonFile')) {
      const targetSlot = slot || 'slot1';
      const targetFilename = filename || 'hello.py';
      const filepath = `/python_root/${targetSlot}/${targetFilename}`;
      toy._robot.executePythonFile(filepath);
    } else {
      toy.unableToRunCommands('executePythonFile');
    }
  };

  toy.enablePythonStreaming = (enable) => {
    if (toy.hasCommand('enablePythonStreaming')) {
      toy._robot.enablePythonStreaming(enable);
    } else {
      toy.unableToRunCommands('enablePythonStreaming');
    }
  };

  toy.getNumberOfPythonStreamedItems = () => {
    if (toy.hasCommand('getNumberOfPythonStreamedItems')) {
      toy._robot.getNumberOfPythonStreamedItems();
    } else {
      toy.unableToRunCommands('getNumberOfPythonStreamedItems');
    }
  };

  toy.getPythonStreamItemData = (indexInStreamingList) => {
    if (toy.hasCommand('getPythonStreamItemData')) {
      toy._robot.getPythonStreamItemData(indexInStreamingList);
    } else {
      toy.unableToRunCommands('getPythonStreamItemData');
    }
  };

  toy.getExecutingPythonTypeAndName = () => {
    if (toy.hasCommand('getExecutingPythonTypeAndName')) {
      toy._robot.getExecutingPythonTypeAndName();
    } else {
      toy.unableToRunCommands('getExecutingPythonTypeAndName');
    }
  };

  toy.getPythonProgramNameAtIndex = (programIndex, timeoutMs) => {
    if (!toy.hasCommand('getPythonProgramNameAtIndex')) {
      toy.unableToRunCommands('getPythonProgramNameAtIndex');
      return Promise.resolve(null);
    }
    const responseEvent = toy.getPythonProgramNameAtIndexResponse;
    if (!responseEvent) {
      toy.unableToRunCommands('getPythonProgramNameAtIndex');
      return Promise.resolve(null);
    }
    return new Promise((resolve) => {
      let timeoutId;
      let settled = false;
      const finish = (response) => {
        if (settled) return;
        settled = true;
        if (timeoutId != null) {
          clearTimeout(timeoutId);
        }
        resolve(response ?? null);
      };
      const handleResponse = (response) => finish(response);
      if (Number.isFinite(timeoutMs) && timeoutMs > 0) {
        timeoutId = setTimeout(() => {
          if (typeof toy._off === 'function') {
            toy._off(responseEvent, handleResponse);
          }
          finish(null);
        }, timeoutMs);
      }
      toy._once(responseEvent, handleResponse);
      toy._robot.getPythonProgramNameAtIndex(programIndex);
    });
  };

  toy.enablePythonPrintToConsole = (enable) => {
    if (toy.hasCommand('enablePythonPrintToConsole')) {
      toy._robot.enablePythonPrintToConsole(enable);
    } else {
      toy.unableToRunCommands('enablePythonPrintToConsole');
    }
  };

  toy.getRemoteDeviceTypes = () => {
    if (toy.hasCommand('getRemoteDeviceTypes')) {
      toy._robot.getRemoteDeviceTypes();
    } else {
      toy.unableToRunCommands('getRemoteDeviceTypes');
    }
  };

  toy.getRemoteDeviceSystemInfoByIndex = (index) => {
    if (toy.hasCommand('getRemoteDeviceSystemInfoByIndex')) {
      toy._robot.getRemoteDeviceSystemInfoByIndex(index);
    } else {
      toy.unableToRunCommands('getRemoteDeviceSystemInfoByIndex');
    }
  };

  toy.getDistanceSensorSettings = (port) => {
    if (toy.hasCommand('getDistanceSensorSettings')) {
      toy._robot.getDistanceSensorSettings(port - 1);
    } else {
      toy.unableToRunCommands('getDistanceSensorSettings');
    }
  };

  toy.setDistanceSensorSettings = (port, threshold) => {
    if (toy.hasCommand('setDistanceSensorSettings')) {
      toy._robot.setDistanceSensorSettings(port - 1, threshold);
    } else {
      toy.unableToRunCommands('setDistanceSensorSettings');
    }
  };

  toy.getBeamBreakSensorSettings = (port) => {
    if (toy.hasCommand('getBeamBreakSensorSettings')) {
      toy._robot.getBeamBreakSensorSettings(port - 1);
    } else {
      toy.unableToRunCommands('getBeamBreakSensorSettings');
    }
  };

  toy.setBeamBreakSensorSettings = (port, threshold) => {
    if (toy.hasCommand('setBeamBreakSensorSettings')) {
      toy._robot.setBeamBreakSensorSettings(port - 1, threshold);
    } else {
      toy.unableToRunCommands('setBeamBreakSensorSettings');
    }
  };

  toy.getMagneticSwitchSettings = (port) => {
    if (toy.hasCommand('getMagneticSwitchSettings')) {
      toy._robot.getMagneticSwitchSettings(port - 1);
    } else {
      toy.unableToRunCommands('getMagneticSwitchSettings');
    }
  };

  toy.getBeeAdapterSettings = (port) => {
    if (toy.hasCommand('getBeeAdapterSettings')) {
      toy._robot.getBeeAdapterSettings(port - 1);
    } else {
      toy.unableToRunCommands('getBeeAdapterSettings');
    }
  };

  toy.setBeeAdapterSettings = (port, output) => {
    if (toy.hasCommand('setBeeAdapterSettings')) {
      // UI uses -100..100; firmware expects -1..1 (float).
      const firmwareOutput = output / 100;
      toy._robot.setBeeAdapterSettings(port - 1, firmwareOutput);
    } else {
      toy.unableToRunCommands('setBeeAdapterSettings');
    }
  };

  toy.getLibraryUpdaterStateDeprecated = () => {
    if (!toy.hasCommand('getLibraryUpdaterStateDeprecated')) {
      toy.unableToRunCommands('getLibraryUpdaterStateDeprecated');
      return Promise.resolve(null);
    }
    const responseEvent = toy.getLibraryUpdaterStateDeprecatedResponse;
    if (!responseEvent) {
      toy.unableToRunCommands('getLibraryUpdaterStateDeprecated');
      return Promise.resolve(null);
    }
    return new Promise((resolve) => {
      toy._once(responseEvent, (response) => {
        resolve(response);
      });
      toy._robot.getLibraryUpdaterStateDeprecated();
    });
  };

  toy.getLibraryUpdaterProgress = () => {
    if (!toy.hasCommand('getLibraryUpdaterProgress')) {
      toy.unableToRunCommands('getLibraryUpdaterProgress');
      return Promise.resolve(null);
    }
    const responseEvent = toy.getLibraryUpdaterProgressResponse;
    if (!responseEvent) {
      toy.unableToRunCommands('getLibraryUpdaterProgress');
      return Promise.resolve(null);
    }
    return new Promise((resolve) => {
      toy._once(responseEvent, (response) => {
        resolve(response);
      });
      toy._robot.getLibraryUpdaterProgress();
    });
  };

  toy.getMotorPlusSettings = (port) => {
    if (!toy.hasCommand('getMotorPlusSettings')) {
      toy.unableToRunCommands('getMotorPlusSettings');
      return Promise.resolve(null);
    }
    const responseEvent = toy.getMotorPlusSettingsResponse;
    if (!responseEvent) {
      toy.unableToRunCommands('getMotorPlusSettings');
      return Promise.resolve(null);
    }

    const sdkPort = port - 1;
    return new Promise((resolve, reject) => {
      pendingGetMotorPlusSettingsRequests.push({ sdkPort, resolve, reject });
      runNextGetMotorPlusSettingsRequest();
    });
  };

  toy.setMotorPlusSettings = (port, settings) => {
    if (!toy.hasCommand('setMotorPlusSettings')) {
      toy.unableToRunCommands('setMotorPlusSettings');
      return Promise.resolve(null);
    }
    const responseEvent = toy.setMotorPlusSettingsResponse;
    if (!responseEvent) {
      toy.unableToRunCommands('setMotorPlusSettings');
      return Promise.resolve(null);
    }

    let normalizedSettings;
    try {
      normalizedSettings = validateMotorPlusSettingsUpdate(settings);
    } catch (error) {
      return Promise.reject(error);
    }

    const { targetPosition, targetVelocity, targetDuty, targetTimeout, targetTolerance, maxTorque, polarityInversion, stopMode, motorMode } =
      normalizedSettings;

    const maxProfileSpeed = 0;
    return new Promise((resolve, reject) => {
      const sdkArguments = [
        port - 1,
        targetPosition,
        targetVelocity,
        targetDuty,
        targetTimeout,
        targetTolerance,
        maxTorque,
        maxProfileSpeed,
        polarityInversion,
        stopMode,
        motorMode,
      ];
      pendingSetMotorPlusSettingsRequests.push({
        sdkArguments,
        resolve,
        reject,
      });
      runNextSetMotorPlusSettingsRequest();
    });
  };

  return toy;
};

export default RobotInterface;
